import React from "react";
import { useForm, FieldError } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Modal from "../common/Modal";
import Input from "../common/Input";
import Button from "../common/Button";
import {
  termsAndConditionsUploadSchema,
  TermsAndConditionsFormData,
} from "../../schemas/termsAndConditionsSchema";

interface TermsAndConditionsFormProps {
  isOpen: boolean;
  onSubmit: (data: TermsAndConditionsFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const TermsAndConditionsForm: React.FC<TermsAndConditionsFormProps> = ({
  isOpen,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    trigger,
    clearErrors,
  } = useForm<TermsAndConditionsFormData>({
    resolver: zodResolver(termsAndConditionsUploadSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      file: undefined,
    },
  });

  const handleFormSubmit = (data: TermsAndConditionsFormData) => {
    onSubmit(data);
    reset();
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title="Upload Terms and Conditions"
      size="md"
    >
      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="space-y-6"
        noValidate
      >
        <div>
          <Input
            label="Terms and Conditions File"
            name="file"
            type="file"
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) {
                setValue("file", e.target.files as any, { shouldValidate: false });
                clearErrors("file");
              } else {
                setValue("file", undefined as any, { shouldValidate: false });
              }
            }}
            onBlur={() => {
              // Don't trigger file validation on blur — clicking the file input
              // opens a native dialog which fires blur immediately before any
              // file is selected, causing a premature "File is required" error.
            }}
            error={errors.file as any}
            accept="application/pdf,.pdf"
          />
          <p className="text-gray-500 text-sm mt-1">
            Accepted format: PDF only (Max size: 50MB)
          </p>
        </div>

        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="secondary"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={isLoading}>
            Upload
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default TermsAndConditionsForm;
